import { isDefined } from '@morpho-org/morpho-ts'

/**
 * What a vault withdraw review takes from the panel logic it re-runs on every block poll.
 *
 * Only the logic's own error (amount, read and build checks) is taken: wallet, chain, pending and
 * gate states are handled by the review itself. A typed amount that the refreshed position or
 * liquidity no longer covers is a hard block: otherwise a full-exit rebuild could sign a smaller
 * redeem than the amount on screen. A MAX amount re-syncs to the live input ceiling one render
 * after it moves, so its error is skipped only until it has caught up. Once synced, an error that
 * persists is real: on an in-kind-redemption V2 vault the MAX ceiling is the full position, so
 * "Insufficient Liquidity" stays after the vault's liquidity runs out. A settled vault or position
 * read error ends the loading state and blocks with its message, so a failed poll never reads as
 * preparation that never finishes.
 */
export function resolveWithdrawReviewBlock({
  isMax,
  inputAmount,
  maxInputAmount,
  isLoading,
  errorMessage,
  userVaultAssets,
  readError
}: {
  isMax: boolean
  inputAmount: bigint
  /** The ceiling MAX re-syncs `inputAmount` to. */
  maxInputAmount: bigint | undefined
  isLoading: boolean
  errorMessage: string | undefined
  userVaultAssets: bigint | undefined
  readError: unknown
}): { isAwaitingPosition: boolean; blockedReason: string | undefined } {
  const hasReadError = isDefined(readError)
  const isAwaitingPosition = !isDefined(userVaultAssets) && !hasReadError
  const isMaxResyncing =
    isMax && !hasReadError && isDefined(maxInputAmount) && inputAmount !== maxInputAmount
  if (isLoading || isAwaitingPosition || !errorMessage || isMaxResyncing) {
    return { isAwaitingPosition, blockedReason: undefined }
  }
  return { isAwaitingPosition, blockedReason: errorMessage }
}
